import {Event, Evented} from '../../util/evented';
import * as DOM from '../../util/dom';
import {bindAll, warnOnce} from '../../util/util';
import assert from '../../style-spec/util/assert';
import Marker from '../marker';
import LngLat from '../../geo/lng_lat';
import throttle from '../../util/throttle';
import {mercatorZfromAltitude} from '../../geo/mercator_coordinate';

import type {Map, IControl} from '../map';
import type {MapEventOf} from '../events';
import type {EasingOptions} from '../camera';

export type GeolocateControlOptions = {
    positionOptions?: PositionOptions;
    fitBoundsOptions?: EasingOptions;
    trackUserLocation?: boolean;
    showAccuracyCircle?: boolean;
    showUserLocation?: boolean;
    showUserHeading?: boolean;
    geolocation?: Geolocation;
    showButton?: boolean;
    followUserLocation?: boolean;
};

declare global {
    interface DeviceOrientationEvent {
        readonly webkitCompassHeading?: number;
    }
}

type DeviceOrientationEventStatic = typeof DeviceOrientationEvent & {
    requestPermission?: () => Promise<'granted' | 'denied' | 'prompt'>;
};

type WatchState = 'OFF' | 'ACTIVE_LOCK' | 'WAITING_ACTIVE' | 'ACTIVE_ERROR' | 'BACKGROUND' | 'BACKGROUND_ERROR';

// The button's appearance is a pure function of the watch state: each state maps
// to the exact set of `mapboxgl-ctrl-geolocate-*` modifier classes it should carry.
// `_setWatchState` applies this mapping, so transitions never need to add/remove
// classes individually.
const watchStateClasses: Record<WatchState, ReadonlyArray<string>> = {
    'OFF': [],
    'WAITING_ACTIVE': ['waiting', 'active'],
    'ACTIVE_LOCK': ['active'],
    'ACTIVE_ERROR': ['waiting', 'active-error'],
    'BACKGROUND': ['background'],
    'BACKGROUND_ERROR': ['waiting', 'background-error']
};

const geolocateClass = (suffix: string) => `mapboxgl-ctrl-geolocate-${suffix}`;

const allGeolocateClasses = ['waiting', 'active', 'active-error', 'background', 'background-error'].map(geolocateClass);

const defaultOptions = {
    positionOptions: {
        enableHighAccuracy: false,
        maximumAge: 0,
        timeout: 6000 /* 6 sec */
    },
    fitBoundsOptions: {
        maxZoom: 15
    },
    trackUserLocation: false,
    showAccuracyCircle: true,
    showUserLocation: true,
    showUserHeading: false,
    showButton: true,
    followUserLocation: true
};

type GeolocateControlEvents = {
    'error': GeolocationPositionError;
    'geolocate': GeolocationPosition;
    'outofmaxbounds': GeolocationPosition;
    'trackuserlocationstart': void;
    'trackuserlocationend': void;
    'ready': void;
};

/**
 * A `GeolocateControl` control provides a button that uses the browser's geolocation
 * API to locate the user on the map.
 * Add this control to a map using {@link Map#addControl}.
 *
 * Not all browsers support geolocation,
 * and some users may disable the feature. Geolocation support for modern
 * browsers including Chrome requires sites to be served over HTTPS. If
 * geolocation support is not available, the `GeolocateControl` will show
 * as disabled.
 *
 * The [zoom level](https://docs.mapbox.com/help/glossary/zoom-level/) applied depends on the accuracy of the geolocation provided by the device.
 *
 * The GeolocateControl has two modes. If `trackUserLocation` is `false` (default) the control acts as a button, which when pressed will set the map's camera to target the user location. If the user moves, the map won't update. This is most suited for the desktop. If `trackUserLocation` is `true` the control acts as a toggle button that when active the user's location is actively monitored for changes. In this mode the `GeolocateControl` has three interaction states:
 * * active - The map's camera automatically updates as the user's location changes, keeping the location dot in the center. This is the initial state, and the state upon clicking the `GeolocateControl` button.
 * * passive - The user's location dot automatically updates, but the map's camera does not. Occurs upon the user initiating a map movement.
 * * disabled - Occurs if geolocation is not available, disabled, or denied.
 *
 * These interaction states can't be controlled programmatically. Instead, they are set based on user interactions.
 *
 * @implements {IControl}
 * @param {Object} [options]
 * @param {PositionOptions} [options.positionOptions={enableHighAccuracy: false, timeout: 6000}] A Geolocation API [PositionOptions](https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions) object.
 * @param {EasingOptions} [options.fitBoundsOptions={maxZoom: 15}] A {@link Map#fitBounds} options object to use when the map is panned and zoomed to the user's location. The default is to use a `maxZoom` of 15 to limit how far the map will zoom in for very accurate locations.
 * @param {boolean} [options.trackUserLocation=false] If `true` the `GeolocateControl` becomes a toggle button and when active the map will receive updates to the user's location as it changes.
 * @param {boolean} [options.showAccuracyCircle=true] By default, if `showUserLocation` is `true`, a transparent circle will be drawn around the user location indicating the accuracy (95% confidence level) of the user's location. Set to `false` to disable. Always disabled when `showUserLocation` is `false`.
 * @param {boolean} [options.showUserLocation=true] By default a dot will be shown on the map at the user's location. Set to `false` to disable.
 * @param {boolean} [options.showUserHeading=false] If `true` an arrow will be drawn next to the user location dot indicating the device's heading. This only has affect when `trackUserLocation` is `true`.
 * @param {Geolocation} [options.geolocation=window.navigator.geolocation] `window.navigator.geolocation` by default; you can provide an object with the same shape to customize geolocation handling.
 * @param {boolean} [options.showButton=true] If `false`, the control button will be hidden. The user location dot can still be shown by setting `showUserLocation` to `true` and calling {@link GeolocateControl#trigger} programmatically.
 * @param {boolean} [options.followUserLocation=true] If `true`, the camera centers on the user's location. If `false`, the location dot will be shown without moving the camera. Clicking the control still centers on the user's location.
 *
 * @example
 * map.addControl(new mapboxgl.GeolocateControl({
 *     positionOptions: {
 *         enableHighAccuracy: true
 *     },
 *     trackUserLocation: true,
 *     showUserHeading: true
 * }));
 *
 * @example
 * // Tracking without visible button - call trigger() to start
 * const geolocate = new mapboxgl.GeolocateControl({
 *     trackUserLocation: true,
 *     showUserLocation: true,
 *     showButton: false
 * });
 * map.addControl(geolocate);
 * geolocate.trigger();
 *
 * @example
 * // Show user location without moving the camera
 * const geolocate = new mapboxgl.GeolocateControl({
 *     trackUserLocation: true,
 *     showUserLocation: true,
 *     followUserLocation: false
 * });
 * map.addControl(geolocate);
 * geolocate.trigger();
 * @see [Example: Locate the user](https://www.mapbox.com/mapbox-gl-js/example/locate-user/)
 */
class GeolocateControl extends Evented<GeolocateControlEvents> implements IControl {
    _map: Map;
    options: GeolocateControlOptions;
    _container: HTMLElement;
    _dotElement: HTMLElement;
    _circleElement: HTMLElement;
    _geolocateButton: HTMLButtonElement;
    _geolocationWatchID: number;
    _timeoutId?: number;
    _requestTimeoutId?: number;
    _watchState: WatchState;
    _lastKnownPosition?: GeolocationPosition;
    _userLocationDotMarker: Marker;
    _accuracyCircleMarker: Marker;
    _accuracy: number;
    _setup: boolean; // set to true once the control has been setup
    _heading?: number;
    _updateMarkerRotationThrottled?: () => number;

    _numberOfWatches: number;
    _noTimeout: boolean;
    _supportsGeolocation: boolean;

    constructor(options: GeolocateControlOptions = {}) {
        super();
        const geolocation = navigator.geolocation;
        this.options = {geolocation, ...defaultOptions, ...options};

        bindAll([
            '_onSuccess',
            '_onError',
            '_onZoom',
            '_finish',
            '_setupUI',
            '_updateCamera',
            '_updateMarker',
            '_updateMarkerRotation',
            '_onDeviceOrientation'
        ], this);

        this._updateMarkerRotationThrottled = throttle(this._updateMarkerRotation, 20);
        this._numberOfWatches = 0;
    }

    onAdd(map: Map): HTMLElement {
        this._map = map;
        this._container = DOM.create('div', `mapboxgl-ctrl mapboxgl-ctrl-group`);
        this._checkGeolocationSupport(this._setupUI);
        return this._container;
    }

    onRemove() {
        this._clearRequestTimeout();

        // clear the geolocation watch if exists
        if (this._geolocationWatchID !== undefined) {
            this.options.geolocation.clearWatch(this._geolocationWatchID);
            this._geolocationWatchID = undefined;
        }

        // clear the markers from the map
        if (this.options.showUserLocation && this._userLocationDotMarker) {
            this._userLocationDotMarker.remove();
        }
        if (this.options.showAccuracyCircle && this._accuracyCircleMarker) {
            this._accuracyCircleMarker.remove();
        }

        this._container.remove();
        this._map.off('zoom', this._onZoom);
        this._map = undefined;
        this._numberOfWatches = 0;
        this._noTimeout = false;
    }

    _checkGeolocationSupport(callback: (arg1: boolean) => void) {
        const updateSupport = (supported: boolean = !!this.options.geolocation) => {
            this._supportsGeolocation = supported;
            callback(supported);
        };

        if (this._supportsGeolocation !== undefined) {
            callback(this._supportsGeolocation);

        } else if (navigator.permissions !== undefined) {
            // navigator.permissions has incomplete browser support http://caniuse.com/#feat=permissions-api
            // Test for the case where a browser disables Geolocation because of an insecure origin;
            // in some environments like iOS16 WebView, permissions reject queries but still support geolocation
            navigator.permissions.query({name: 'geolocation'})
                .then(p => updateSupport(p.state !== 'denied'))
                .catch(() => updateSupport());

        } else {
            updateSupport();
        }
    }

    /**
     * Check if the Geolocation API Position is outside the map's `maxBounds`.
     *
     * @param {Position} position the Geolocation API Position
     * @returns {boolean} Returns `true` if position is outside the map's `maxBounds`, otherwise returns `false`.
     * @private
     */
    _isOutOfMapMaxBounds(position: GeolocationPosition): boolean {
        const bounds = this._map.getMaxBounds();
        const coordinates = position.coords;

        return !!bounds && (
            coordinates.longitude < bounds.getWest() ||
            coordinates.longitude > bounds.getEast() ||
            coordinates.latitude < bounds.getSouth() ||
            coordinates.latitude > bounds.getNorth()
        );
    }

    // Set the watch state and sync the button's modifier classes to match it.
    _setWatchState(state: WatchState) {
        this._watchState = state;
        const list = this._geolocateButton.classList;
        list.remove(...allGeolocateClasses);
        for (const suffix of watchStateClasses[state]) {
            list.add(geolocateClass(suffix));
        }
    }

    // Set the button's accessible label and tooltip from a localized UI string.
    _setButtonTitle(stringKey: Parameters<Map['_getUIString']>[0]) {
        const title = this._map._getUIString(stringKey);
        this._geolocateButton.setAttribute('aria-label', title);
        if (this._geolocateButton.firstElementChild) this._geolocateButton.firstElementChild.setAttribute('title', title);
    }

    _setErrorState() {
        switch (this._watchState) {
        case 'WAITING_ACTIVE':
        case 'ACTIVE_LOCK':
            this._setWatchState('ACTIVE_ERROR');
            // turn marker grey
            break;
        case 'BACKGROUND':
            this._setWatchState('BACKGROUND_ERROR');
            // turn marker grey
            break;
        case 'ACTIVE_ERROR':
            break;
        default:
            assert(false, `Unexpected watchState ${this._watchState}`);
        }
    }

    /**
     * When the Geolocation API returns a new location, update the `GeolocateControl`.
     *
     * @param {Position} position the Geolocation API Position
     * @private
     */
    _onSuccess(position: GeolocationPosition) {
        if (!this._map) {
            // control has since been removed
            return;
        }

        this._clearRequestTimeout();

        if (this._isOutOfMapMaxBounds(position)) {
            this._setErrorState();

            this.fire(new Event('outofmaxbounds', position));
            this._updateMarker();
            this._finish();

            return;
        }

        // Keep a record of the last good position. Used by the trackUserLocation
        // BACKGROUND->ACTIVE_LOCK transition, and by runtime setters that need
        // to re-render markers without waiting for a new geolocation event.
        this._lastKnownPosition = position;

        if (this.options.trackUserLocation) {
            switch (this._watchState) {
            case 'WAITING_ACTIVE':
            case 'ACTIVE_LOCK':
            case 'ACTIVE_ERROR':
                // If followUserLocation is false, go directly to BACKGROUND since camera won't follow
                this._setWatchState(this.options.followUserLocation ? 'ACTIVE_LOCK' : 'BACKGROUND');
                break;
            case 'BACKGROUND':
            case 'BACKGROUND_ERROR':
                this._setWatchState('BACKGROUND');
                break;
            default:
                assert(false, `Unexpected watchState ${this._watchState}`);
            }
        }

        // if showUserLocation and the watch state isn't off then update the marker location
        if (this.options.showUserLocation && this._watchState !== 'OFF') {
            this._updateMarker(position);
        }

        // Update camera in one-time mode, or when actively locked in watch mode
        const isOneTimeMode = !this.options.trackUserLocation;
        const isActivelyTracking = this._watchState === 'ACTIVE_LOCK';
        const shouldFollowUser = this.options.followUserLocation;

        if (shouldFollowUser && (isOneTimeMode || isActivelyTracking)) {
            this._updateCamera(position);
        }

        if (this.options.showUserLocation) {
            this._userLocationDotMarker.removeClassName('mapboxgl-user-location-dot-stale');
        }

        this.fire(new Event('geolocate', ({coords: position.coords, timestamp: position.timestamp, ...(position.toJSON ? {toJSON: position.toJSON.bind(position)} : {})}) as GeolocationPosition));
        this._finish();
    }

    /**
     * Update the camera location to center on the current position
     *
     * @param {Position} position the Geolocation API Position
     * @private
     */
    _updateCamera(position: GeolocationPosition) {
        const center = new LngLat(position.coords.longitude, position.coords.latitude);
        const radius = position.coords.accuracy;
        const bearing = this._map.getBearing();
        const options = {bearing, ...this.options.fitBoundsOptions};

        this._map.fitBounds(center.toBounds(radius), options, {
            geolocateSource: true // tag this camera change so it won't cause the control to change to background state
        });
    }

    /**
     * Update the user location dot Marker to the current position
     *
     * @param {Position} [position] the Geolocation API Position
     * @private
     */
    _updateMarker(position?: GeolocationPosition | null) {
        if (position) {
            const center = new LngLat(position.coords.longitude, position.coords.latitude);
            if (this.options.showAccuracyCircle) {
                this._accuracyCircleMarker.setLngLat(center).addTo(this._map);
            } else {
                this._accuracyCircleMarker.remove();
            }
            this._userLocationDotMarker.setLngLat(center).addTo(this._map);
            this._accuracy = position.coords.accuracy;
            if (this.options.showUserLocation && this.options.showAccuracyCircle) {
                this._updateCircleRadius();
            }
        } else {
            this._userLocationDotMarker.remove();
            this._accuracyCircleMarker.remove();
        }
    }

    _updateCircleRadius() {
        assert(this._circleElement);
        const map = this._map;
        const tr = map.transform;

        const pixelsPerMeter = mercatorZfromAltitude(1.0, tr._center.lat) * tr.worldSize;
        assert(pixelsPerMeter !== 0.0);
        const circleDiameter = Math.ceil(2.0 * this._accuracy * pixelsPerMeter);

        this._circleElement.style.width = `${circleDiameter}px`;
        this._circleElement.style.height = `${circleDiameter}px`;
    }

    _onZoom() {
        if (this.options.showUserLocation && this.options.showAccuracyCircle) {
            this._updateCircleRadius();
        }
    }

    /**
     * Update the user location dot Marker rotation to the current heading
     *
     * @private
     */
    _updateMarkerRotation() {
        if (this._userLocationDotMarker && typeof this._heading === 'number') {
            this._userLocationDotMarker.setRotation(this._heading);
            this._userLocationDotMarker.addClassName('mapboxgl-user-location-show-heading');
        } else {
            this._userLocationDotMarker.removeClassName('mapboxgl-user-location-show-heading');
            this._userLocationDotMarker.setRotation(0);
        }
    }

    _onError(error: GeolocationPositionError) {
        if (!this._map) {
            // control has since been removed
            return;
        }

        this._clearRequestTimeout();

        if (this.options.trackUserLocation) {
            if (error.code === 1) {
                // PERMISSION_DENIED
                this._setWatchState('OFF');
                this._geolocateButton.disabled = true;
                this._setButtonTitle('GeolocateControl.LocationNotAvailable');

                if (this._geolocationWatchID !== undefined) {
                    this._clearWatch();
                }
            } else if (error.code === 3 && this._noTimeout) {
                // this represents a forced error state
                // this was triggered to force immediate geolocation when a watch is already present
                // see https://github.com/mapbox/mapbox-gl-js/issues/8214
                // and https://w3c.github.io/geolocation-api/#example-5-forcing-the-user-agent-to-return-a-fresh-cached-position
                return;
            } else {
                this._setErrorState();
            }
        }

        if (this._watchState !== 'OFF' && this.options.showUserLocation) {
            this._userLocationDotMarker.addClassName('mapboxgl-user-location-dot-stale');
        }

        this.fire(new Event('error', {
            code: error.code,
            message: error.message,
            PERMISSION_DENIED: error.PERMISSION_DENIED,
            POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
            TIMEOUT: error.TIMEOUT
        }));

        this._finish();
    }

    _finish() {
        if (this._timeoutId) { clearTimeout(this._timeoutId); }
        this._timeoutId = undefined;
    }

    // Workaround for browsers that fail to call geolocation callbacks (https://github.com/mapbox/mapbox-gl-js/issues/12531)
    _startRequestTimeout() {
        this._clearRequestTimeout();
        const timeout = this.options.positionOptions.timeout;
        if (!timeout) return;

        this._requestTimeoutId = window.setTimeout(() => {
            const error = {code: 3, message: 'Geolocation request timed out'} as GeolocationPositionError;
            this._onError(error);
        }, timeout);
    }

    _clearRequestTimeout() {
        if (this._requestTimeoutId !== undefined) {
            clearTimeout(this._requestTimeoutId);
            this._requestTimeoutId = undefined;
        }
    }

    _setupUI(supported: boolean) {
        if (this._map === undefined) {
            // This control was removed from the map before geolocation
            // support was determined.
            return;
        }
        this._container.addEventListener('contextmenu', (e: MouseEvent) => e.preventDefault());
        this._geolocateButton = DOM.create('button', `mapboxgl-ctrl-geolocate`, this._container);
        DOM.create('span', `mapboxgl-ctrl-icon`, this._geolocateButton).setAttribute('aria-hidden', 'true');

        this._geolocateButton.type = 'button';

        if (supported === false) {
            warnOnce('Geolocation support is not available so the GeolocateControl will be disabled.');
            this._geolocateButton.disabled = true;
            this._setButtonTitle('GeolocateControl.LocationNotAvailable');
        } else {
            this._setButtonTitle('GeolocateControl.FindMyLocation');
        }

        if (this.options.trackUserLocation) {
            this._geolocateButton.setAttribute('aria-pressed', 'false');
            this._watchState = 'OFF';
        }

        // when showUserLocation is enabled, keep the Geolocate button disabled until the device location marker is setup on the map
        if (this.options.showUserLocation) {
            this._createUserLocationMarkers();

            if (this.options.trackUserLocation) this._watchState = 'OFF';

            this._map.on('zoom', this._onZoom);
        }

        this._geolocateButton.addEventListener('click', this.trigger.bind(this));

        this._setup = true;

        if (!this.options.showButton) {
            this._container.style.display = 'none';
        }

        // when the camera is changed (and it's not as a result of the Geolocation Control) change
        // the watch mode to background watch, so that the marker is updated but not the camera.
        if (this.options.trackUserLocation) {
            this._map.on('movestart', (event: MapEventOf<'movestart'> & {geolocateSource?: boolean}) => {
                const fromResize = event.originalEvent && event.originalEvent.type === 'resize';
                if (!event.geolocateSource && this._watchState === 'ACTIVE_LOCK' && !fromResize) {
                    this._setWatchState('BACKGROUND');

                    this.fire(new Event('trackuserlocationend'));
                }
            });
        }

        this.fire(new Event('ready'));
    }

    /**
    * Programmatically request and move the map to the user's location.
    *
    * @returns {boolean} Returns `false` if called before control was added to a map, otherwise returns `true`.
    * Called on a `deviceorientation` event.
    *
    * @param deviceOrientationEvent {DeviceOrientationEvent}
    * @private
    * @example
    * // Initialize the GeolocateControl.
    * var geolocate = new mapboxgl.GeolocateControl({
    *  positionOptions: {
    *    enableHighAccuracy: true
    *  },
    *  trackUserLocation: true
    * });
    * // Add the control to the map.
    * map.addControl(geolocate);
    * map.on('load', function() {
    *   geolocate.trigger();
    * });
    */
    _onDeviceOrientation(deviceOrientationEvent: DeviceOrientationEvent) {
        // absolute is true if the orientation data is provided as the difference between the Earth's coordinate frame and the device's coordinate frame, or false if the orientation data is being provided in reference to some arbitrary, device-determined coordinate frame.
        if (this._userLocationDotMarker) {
            if (deviceOrientationEvent.webkitCompassHeading) {
                // Safari
                this._heading = deviceOrientationEvent.webkitCompassHeading;
            } else if (deviceOrientationEvent.absolute === true) {
                // non-Safari alpha increases counter clockwise around the z axis
                this._heading = deviceOrientationEvent.alpha * -1;
            }
            this._updateMarkerRotationThrottled();
        }
    }

    /**
     * Trigger a geolocation event.
     *
     * @example
     * // Initialize the geolocate control.
     * const geolocate = new mapboxgl.GeolocateControl({
     *     positionOptions: {
     *         enableHighAccuracy: true
     *     },
     *     trackUserLocation: true
     * });
     * // Add the control to the map.
     * map.addControl(geolocate);
     * // Wait until the control is set up before triggering it.
     * geolocate.once('ready', () => {
     *     geolocate.trigger();
     * });
     * @returns {boolean} Returns `false` if called before control was added to a map, otherwise returns `true`.
     */
    trigger(): boolean {
        if (!this._setup) {
            warnOnce('Geolocate control triggered before added to a map');
            return false;
        }
        if (this.options.trackUserLocation) {
            // update watchState (which syncs the button classes) and do any other state cleanup
            switch (this._watchState) {
            case 'OFF':
                // turn on the GeolocateControl
                this._setWatchState('WAITING_ACTIVE');

                this.fire(new Event('trackuserlocationstart'));
                break;
            case 'WAITING_ACTIVE':
            case 'ACTIVE_LOCK':
            case 'ACTIVE_ERROR':
            case 'BACKGROUND_ERROR':
                // turn off the Geolocate Control
                this._numberOfWatches--;
                this._noTimeout = false;
                this._setWatchState('OFF');

                this.fire(new Event('trackuserlocationend'));
                break;
            case 'BACKGROUND':
                this._setWatchState('ACTIVE_LOCK');
                // Button click is explicit user action - always center on their location
                if (this._lastKnownPosition) {
                    this._updateCamera(this._lastKnownPosition);
                }

                this.fire(new Event('trackuserlocationstart'));
                break;
            default:
                assert(false, `Unexpected watchState ${this._watchState}`);
            }

            // manage geolocation.watchPosition / geolocation.clearWatch
            if (this._watchState === 'OFF' && this._geolocationWatchID !== undefined) {
                // clear watchPosition as we've changed to an OFF state
                this._clearWatch();
            } else if (this._geolocationWatchID === undefined) {
                // enable watchPosition since watchState is not OFF and there is no watchPosition already running

                this._geolocateButton.classList.add(geolocateClass('waiting'));
                this._geolocateButton.setAttribute('aria-pressed', 'true');

                this._numberOfWatches++;
                let positionOptions: PositionOptions | undefined;
                if (this._numberOfWatches > 1) {
                    positionOptions = {maximumAge: 600000, timeout: 0};
                    this._noTimeout = true;
                } else {
                    positionOptions = this.options.positionOptions;
                    this._noTimeout = false;
                }

                this._geolocationWatchID = this.options.geolocation.watchPosition(
                    this._onSuccess, this._onError, positionOptions);

                this._startRequestTimeout();

                if (this.options.showUserHeading) {
                    this._addDeviceOrientationListener();
                }
            }
        } else {
            this.options.geolocation.getCurrentPosition(this._onSuccess, this._onError, this.options.positionOptions);

            this._startRequestTimeout();

            // This timeout ensures that we still call finish() even if
            // the user declines to share their location in Firefox
            this._timeoutId = window.setTimeout(this._finish, 10000 /* 10sec */);
        }

        return true;
    }

    /**
     * Sets whether the camera follows the user's location.
     *
     * Fires {@link GeolocateControl#trackuserlocationstart} when enabling,
     * {@link GeolocateControl#trackuserlocationend} when disabling.
     *
     * @param {boolean} [follow] Whether to follow the user's location.
     * @returns {GeolocateControl} `this`.
     * @example
     * geolocate.setFollowUserLocation(false); // stop following
     * geolocate.setFollowUserLocation(true);  // resume and center
     */
    setFollowUserLocation(follow?: boolean): this {
        this.options.followUserLocation = follow != null ? follow : defaultOptions.followUserLocation;

        if (this.options.trackUserLocation && this._watchState !== 'OFF') {
            if (this.options.followUserLocation) {
                // Enabling: center camera and transition to ACTIVE_LOCK if in BACKGROUND
                if (this._watchState === 'BACKGROUND' || this._watchState === 'BACKGROUND_ERROR') {
                    this._setWatchState('ACTIVE_LOCK');

                    if (this._lastKnownPosition) {
                        this._updateCamera(this._lastKnownPosition);
                    }

                    this.fire(new Event('trackuserlocationstart'));
                }
            } else {
                // Disabling: transition to BACKGROUND if in ACTIVE_LOCK
                if (this._watchState === 'ACTIVE_LOCK' || this._watchState === 'ACTIVE_ERROR') {
                    this._setWatchState('BACKGROUND');

                    this.fire(new Event('trackuserlocationend'));
                }
            }
        }

        return this;
    }

    /**
     * Sets whether a transparent accuracy circle is drawn around the user
     * location dot.
     *
     * Has no effect if the control was constructed with `showUserLocation: false`,
     * because the underlying marker is only created when user-location rendering
     * is enabled. Call `setShowUserLocation(true)` first if you need the circle
     * after construction.
     *
     * @param {boolean} [show] Whether to draw the accuracy circle.
     * @returns {GeolocateControl} `this`.
     * @example
     * geolocate.setShowAccuracyCircle(false);
     */
    setShowAccuracyCircle(show?: boolean): this {
        this.options.showAccuracyCircle = show != null ? show : defaultOptions.showAccuracyCircle;

        if (!this._accuracyCircleMarker) return this;

        if (this.options.showAccuracyCircle) {
            if (this.options.showUserLocation && this._lastKnownPosition) {
                const {longitude, latitude, accuracy} = this._lastKnownPosition.coords;
                this._accuracy = accuracy;
                this._accuracyCircleMarker.setLngLat(new LngLat(longitude, latitude)).addTo(this._map);
                this._updateCircleRadius();
            }
        } else {
            this._accuracyCircleMarker.remove();
        }

        return this;
    }

    /**
     * Sets whether an arrow next to the user location dot indicates the
     * device's heading.
     *
     * Only takes effect when `trackUserLocation` is `true` (heading is only
     * meaningful while a geolocation watch is active).
     *
     * @param {boolean} [show] Whether to show the heading arrow.
     * @returns {GeolocateControl} `this`.
     * @example
     * geolocate.setShowUserHeading(true);
     */
    setShowUserHeading(show?: boolean): this {
        this.options.showUserHeading = show != null ? show : defaultOptions.showUserHeading;

        if (!this._setup) return this;

        if (this.options.showUserHeading) {
            // If a geolocation watch is already active, attach the orientation
            // listener now so the heading arrow appears immediately.
            if (this._geolocationWatchID !== undefined) {
                this._addDeviceOrientationListener();
            }
        } else {
            this._removeDeviceOrientationListener();
            this._heading = undefined;
            if (this._userLocationDotMarker) this._updateMarkerRotation();
        }

        return this;
    }

    /**
     * Sets the {@link Map#fitBounds} options used when the map is panned and
     * zoomed to the user's location. Takes effect on the next geolocation
     * event or the next call to {@link GeolocateControl#trigger}.
     *
     * @param {EasingOptions} [opts] The new `fitBounds` options.
     * @returns {GeolocateControl} `this`.
     * @example
     * geolocate.setFitBoundsOptions({maxZoom: 17, duration: 0});
     */
    setFitBoundsOptions(opts?: EasingOptions): this {
        this.options.fitBoundsOptions = opts != null ? opts : defaultOptions.fitBoundsOptions;
        return this;
    }

    /**
     * Sets whether the user location dot (and, when enabled, the accuracy
     * circle and heading arrow) is rendered on the map.
     *
     * When called with `true` on a control that was constructed with
     * `showUserLocation: false`, the location markers are created lazily.
     *
     * @param {boolean} [show] Whether to render the user location dot.
     * @returns {GeolocateControl} `this`.
     * @example
     * geolocate.setShowUserLocation(false); // hide the puck
     * geolocate.setShowUserLocation(true);  // show it again
     */
    setShowUserLocation(show?: boolean): this {
        this.options.showUserLocation = show != null ? show : defaultOptions.showUserLocation;

        if (!this._setup) return this;

        if (this.options.showUserLocation) {
            if (!this._userLocationDotMarker) {
                this._createUserLocationMarkers();
                this._map.on('zoom', this._onZoom);
            }
            if (this._lastKnownPosition && (this._watchState === undefined || this._watchState !== 'OFF')) {
                this._updateMarker(this._lastKnownPosition);
            }
        } else {
            if (this._userLocationDotMarker) this._userLocationDotMarker.remove();
            if (this._accuracyCircleMarker) this._accuracyCircleMarker.remove();
        }

        return this;
    }

    _addDeviceOrientationListener() {
        const addListener = () => {
            const eventName = 'ondeviceorientationabsolute' in window ?
                'deviceorientationabsolute' :
                'deviceorientation';

            window.addEventListener(eventName, this._onDeviceOrientation);
        };

        if (typeof (DeviceOrientationEvent as DeviceOrientationEventStatic).requestPermission === 'function') {
            (DeviceOrientationEvent as DeviceOrientationEventStatic).requestPermission()
                .then(response => {
                    if (response === 'granted') addListener();
                })
                .catch(console.error);
        } else {
            addListener();
        }
    }

    _removeDeviceOrientationListener() {
        window.removeEventListener('deviceorientation', this._onDeviceOrientation);
        window.removeEventListener('deviceorientationabsolute', this._onDeviceOrientation);
    }

    _createUserLocationMarkers() {
        this._dotElement = DOM.create('div', 'mapboxgl-user-location');
        this._dotElement.appendChild(DOM.create('div', 'mapboxgl-user-location-dot'));
        this._dotElement.appendChild(DOM.create('div', 'mapboxgl-user-location-heading'));

        this._userLocationDotMarker = new Marker({
            element: this._dotElement,
            rotationAlignment: 'map',
            pitchAlignment: 'map'
        });

        this._circleElement = DOM.create('div', 'mapboxgl-user-location-accuracy-circle');
        this._accuracyCircleMarker = new Marker({element: this._circleElement, pitchAlignment: 'map'});
    }

    _clearWatch() {
        this._clearRequestTimeout();
        this.options.geolocation.clearWatch(this._geolocationWatchID);

        this._removeDeviceOrientationListener();

        this._geolocationWatchID = undefined;
        this._geolocateButton.classList.remove(geolocateClass('waiting'));
        this._geolocateButton.setAttribute('aria-pressed', 'false');

        if (this.options.showUserLocation) {
            this._updateMarker(null);
        }
    }
}

export default GeolocateControl;

/* GeolocateControl Watch States
 * This is the private state of the control.
 *
 * OFF
 *    off/inactive
 * WAITING_ACTIVE
 *    GeolocateControl was clicked but still waiting for Geolocation API response with user location
 * ACTIVE_LOCK
 *    Showing the user location as a dot AND tracking the camera to be fixed to their location. If their location changes the map moves to follow.
 * ACTIVE_ERROR
 *    There was en error from the Geolocation API while trying to show and track the user location.
 * BACKGROUND
 *    Showing the user location as a dot but the camera doesn't follow their location as it changes.
 * BACKGROUND_ERROR
 *    There was an error from the Geolocation API while trying to show (but not track) the user location.
 */

/**
 * Fired on each Geolocation API position update that returned as success.
 *
 * @event geolocate
 * @memberof GeolocateControl
 * @instance
 * @property {GeolocationPosition} data The returned [Position](https://developer.mozilla.org/en-US/docs/Web/API/Position) object from the callback in [Geolocation.getCurrentPosition()](https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/getCurrentPosition) or [Geolocation.watchPosition()](https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/watchPosition).
 * @example
 * // Initialize the GeolocateControl.
 * const geolocate = new mapboxgl.GeolocateControl({
 *     positionOptions: {
 *         enableHighAccuracy: true
 *     },
 *     trackUserLocation: true
 * });
 * // Add the control to the map.
 * map.addControl(geolocate);
 * // Set an event listener that fires
 * // when a geolocate event occurs.
 * geolocate.on('geolocate', () => {
 *     console.log('A geolocate event has occurred.');
 * });
 */

/**
 * Fired on each Geolocation API position update that returned as an error.
 *
 * @event error
 * @memberof GeolocateControl
 * @instance
 * @property {GeolocationPositionError} data The returned [PositionError](https://developer.mozilla.org/en-US/docs/Web/API/PositionError) object from the callback in [Geolocation.getCurrentPosition()](https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/getCurrentPosition) or [Geolocation.watchPosition()](https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/watchPosition).
 * @example
 * // Initialize the GeolocateControl.
 * const geolocate = new mapboxgl.GeolocateControl({
 *     positionOptions: {
 *         enableHighAccuracy: true
 *     },
 *     trackUserLocation: true
 * });
 * // Add the control to the map.
 * map.addControl(geolocate);
 * // Set an event listener that fires
 * // when an error event occurs.
 * geolocate.on('error', () => {
 *     console.log('An error event has occurred.');
 * });
 */

/**
 * Fired on each Geolocation API position update that returned as success but user position is out of map `maxBounds`.
 *
 * @event outofmaxbounds
 * @memberof GeolocateControl
 * @instance
 * @property {GeolocationPosition} data The returned [Position](https://developer.mozilla.org/en-US/docs/Web/API/Position) object from the callback in [Geolocation.getCurrentPosition()](https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/getCurrentPosition) or [Geolocation.watchPosition()](https://developer.mozilla.org/en-US/docs/Web/API/Geolocation/watchPosition).
 * @example
 * // Initialize the GeolocateControl.
 * const geolocate = new mapboxgl.GeolocateControl({
 *     positionOptions: {
 *         enableHighAccuracy: true
 *     },
 *     trackUserLocation: true
 * });
 * // Add the control to the map.
 * map.addControl(geolocate);
 * // Set an event listener that fires
 * // when an outofmaxbounds event occurs.
 * geolocate.on('outofmaxbounds', () => {
 *     console.log('An outofmaxbounds event has occurred.');
 * });
 */

/**
 * Fired when the `GeolocateControl` changes to the active lock state, which happens either upon first obtaining a successful Geolocation API position for the user (a `geolocate` event will follow), or when the user clicks the geolocate button when in the background state, which uses the last known position to recenter the map and enter active lock state (no `geolocate` event will follow unless the users's location changes).
 *
 * @event trackuserlocationstart
 * @memberof GeolocateControl
 * @instance
 * @example
 * // Initialize the GeolocateControl.
 * const geolocate = new mapboxgl.GeolocateControl({
 *     positionOptions: {
 *         enableHighAccuracy: true
 *     },
 *     trackUserLocation: true
 * });
 * // Add the control to the map.
 * map.addControl(geolocate);
 * // Set an event listener that fires
 * // when a trackuserlocationstart event occurs.
 * geolocate.on('trackuserlocationstart', () => {
 *     console.log('A trackuserlocationstart event has occurred.');
 * });
 */

/**
 * Fired once when the `GeolocateControl` has finished initializing and is ready to be triggered. Because geolocation support is determined asynchronously (via the Permissions API on supporting browsers), this event provides a reliable signal that {@link GeolocateControl#trigger} can be called without warning.
 *
 * @event ready
 * @memberof GeolocateControl
 * @instance
 * @example
 * // Initialize the GeolocateControl.
 * const geolocate = new mapboxgl.GeolocateControl({
 *     trackUserLocation: true
 * });
 * // Add the control to the map.
 * map.addControl(geolocate);
 * // Trigger the control as soon as it is ready.
 * geolocate.once('ready', () => {
 *     geolocate.trigger();
 * });
 */

/**
 * Fired when the `GeolocateControl` changes to the background state, which happens when a user changes the camera during an active position lock. This only applies when `trackUserLocation` is `true`. In the background state, the dot on the map will update with location updates but the camera will not.
 *
 * @event trackuserlocationend
 * @memberof GeolocateControl
 * @instance
 * @example
 * // Initialize the GeolocateControl.
 * const geolocate = new mapboxgl.GeolocateControl({
 *     positionOptions: {
 *         enableHighAccuracy: true
 *     },
 *     trackUserLocation: true
 * });
 * // Add the control to the map.
 * map.addControl(geolocate);
 * // Set an event listener that fires
 * // when a trackuserlocationend event occurs.
 * geolocate.on('trackuserlocationend', () => {
 *     console.log('A trackuserlocationend event has occurred.');
 * });
 */
